<template>
	<button
		@click="clickButtonSendHandler"
		:class="[
			'button-send',
			{
				'button-send--stop': sendingMessage,
				'button-send--active': canSend,
				'button-send--mic': !textEntered && !sendingMessage,
				'button-send--mic--active': !textEntered && !loading,
				'button-send--disable': sendingMessage && !isStreaming
			}
		]"
	>
		<Transition mode="out-in">
			<div v-if="textEntered && !sendingMessage" key="send">
				<svg-icon v-if="searchTopic" name="search" :size="24" />
				<svg-icon v-else name="sendMessage" :size="24" />
			</div>
			<div v-else>
				<Transition mode="out-in">
					<svg-icon v-if="isRecording || sendingMessage" name="stopRecord" key="stopRecord" :size="24" />
					<svg-icon v-else name="mic" key="mic" :size="24" />
				</Transition>
			</div>
		</Transition>
		<dialog-component
			v-if="isForbidden"
			title="Доступ к микрофону запрещён"
			@close="isForbidden = false"
			@clickFirstBtn="isForbidden = false"
			firstBtnText="Закрыть"
			:showSecondBtn="false"
		/>
	</button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import SvgIcon from '@/components/SvgIcon.vue'
import DialogComponent from '@/components/dialogs/DialogComponent.vue'
import { useThreadStore } from '@/stores/thread'
import { useCommonStore } from '@/stores/common'
import { useAuthRedirect } from '@/composables/useAuthRedirect'
import { useAudioRecording } from '@/composables/useAudioRecording'
const props = defineProps({
	canSend: { type: Boolean, required: true },
	textEntered: { type: Boolean, required: true },
	sendingMessage: { type: Boolean, required: true }
})

const emit = defineEmits<{
	sendMessage: []
	getText: [text: string]
}>()
const threadStore = useThreadStore()
const commonStore = useCommonStore()
const router = useRouter()

const { ensureAuthenticated } = useAuthRedirect()

// Транскрибация под гейтом генераций не стоит намеренно: она не заказывает генерацию,
// а заполняет поле ввода — отправка из него уже проходит checkResource в ThreadView/HomeView.
const { isRecording, isForbidden, start, stop } = useAudioRecording({
	onAudioReady: async (blob) => {
		try {
			const message = await threadStore.sendAudio({ audio: blob })
			emit('getText', message.text)
		} catch (error) {
			console.error(error)
		}
	}
})

const searchTopic = computed(
	() => threadStore.thread?.topic?.type === 'search' && router.currentRoute.value.name !== 'Home'
)
const loading = computed(() => commonStore.loading)
const isStreaming = computed(() => threadStore.isStreaming)

async function clickButtonSendHandler() {
	if (props.sendingMessage) {
		await threadStore.cancelThreadStream()
		return
	}

	if (loading.value) return

	if (isRecording.value) {
		stop()
		return
	}
	if (!props.textEntered) {
		try {
			await ensureAuthenticated()
			await start()
		} catch {
			// не аутентифицирован — редирект уже выполнен в ensureAuthenticated
		}
		return
	}
	emit('sendMessage')
}
</script>

<style lang="scss" scoped>
.button-send {
	background: none;
	border: none;
	color: var(--text-primary);
	transition: $transition-color, $transition-opacity, $transition-bg;
	width: 24px;
	height: 24px;
	opacity: 50%;
	border-radius: var(--radius-md);
	cursor: pointer;
	&--stop {
		background-color: var(--background-muted);
		color: var(--text-inverse);
		opacity: 1;
	}
	&--active {
		color: var(--text-brand);
		cursor: pointer;
		opacity: 1;
		&:hover {
			color: var(--text-brand);
		}
		&:active {
			color: var(--text-brand);
		}
	}
	&--mic {
		color: var(--text-primary);
	}
	&--mic--active {
		color: var(--text-inverse);
		background-color: var(--action-primary);
		opacity: 100%;
		cursor: pointer;
		&:hover {
			color: var(--text-inverse);
			background-color: var(--action-primary-hover);
		}
		&:active {
			color: var(--text-inverse);
			background-color: var(--action-primary-hover);
		}
	}
	&--disable {
		cursor: default;
		opacity: 0.5;
	}
}
.v-enter-active,
.v-leave-active {
	transition: opacity 0.2s ease;
}
.v-enter-from,
.v-leave-to {
	opacity: 0;
}
</style>
